import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';

interface TransformateursCategoryProps {
  t: any;
  isRTL: boolean;
}

const CategoryPage = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  background: #f8fafc;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  position: relative;
  overflow-x: hidden;
  width: 100%;
  padding-top: 120px;
  
  @media (max-width: 768px) {
    padding-top: 100px;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1.5rem;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const HeaderSection = styled.div<{ $isRTL: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const Breadcrumbs = styled(motion.div)<{ $isRTL: boolean }>`
  padding: 1rem 0;
  font-size: 0.9rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const BreadcrumbLink = styled.span<{ $isLast: boolean }>`
  color: ${props => props.$isLast ? '#dc2626' : '#64748b'};
  text-decoration: none;
  font-weight: ${props => props.$isLast ? '600' : '400'};
  cursor: ${props => props.$isLast ? 'default' : 'pointer'};

  &:hover {
    text-decoration: ${props => props.$isLast ? 'none' : 'underline'};
  }

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const CategoryTitle = styled(motion.h1)<{ $isRTL: boolean }>`
  font-size: 2.5rem;
  color: #0f172a;
  font-weight: 700;
  letter-spacing: -0.02em;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
  
  background: linear-gradient(135deg, #0f172a 0%, #dc2626 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const FilterSection = styled.div<{ $isRTL: boolean }>`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
    position: relative;
  }
`;

const MobileFilterToggle = styled.button<{ $isRTL: boolean }>`
  display: none;
  background: #dc2626;
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  justify-content: center;
  
  @media (max-width: 768px) {
    display: flex;
  }
  
  &:hover {
    background: #b91c1c;
  }
`;

const FilterContainer = styled.div<{ $isRTL: boolean; $isOpen: boolean }>`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'flex' : 'none'};
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 1rem;
    padding: 1rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border: 1px solid #e2e8f0;
  }
`;

const FilterButton = styled.button<{ $isRTL: boolean; $active?: boolean; $hasDropdown?: boolean }>`
  background: ${props => props.$active ? '#dc2626' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#64748b'};
  border: 1px solid ${props => props.$active ? '#dc2626' : '#e2e8f0'};
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: ${props => props.$active ? '#b91c1c' : '#f8fafc'};
    border-color: ${props => props.$active ? '#b91c1c' : '#cbd5e1'};
  }
  
  @media (max-width: 768px) {
    padding: 0.8rem 1rem;
    font-size: 0.9rem;
    width: 100%;
    justify-content: center;
    border-radius: 12px;
  }
`;

const DropdownIcon = styled.span`
  font-size: 0.8rem;
  transition: transform 0.3s ease;
`;

const ProductsCount = styled.p<{ $isRTL: boolean }>`
  font-size: 0.9rem;
  color: #94a3b8;
  margin: 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
  
  @media (max-width: 768px) {
    text-align: center;
    margin-top: 1rem;
  }
`;

const ProductsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const ProductCard = styled(motion.div)<{ $isRTL: boolean }>`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #f1f5f9;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  
  &:hover {
    box-shadow: 0 10px 25px -3px rgba(0, 0, 0, 0.1);
    transform: translateY(-4px);
    border-color: #e2e8f0;
  }
  
  @media (max-width: 768px) {
    border-radius: 12px;
    
    &:hover {
      transform: translateY(-2px);
    }
  }
`;

const ProductImage = styled.div`
  height: 200px;
  width: 100%;
  overflow: hidden;
  position: relative;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
    border-radius: 8px;
  }
  
  ${ProductCard}:hover & img {
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    height: 180px;
    padding: 0.75rem;
  }
`;

const ProductContent = styled.div<{ $isRTL: boolean }>`
  padding: 1.5rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ProductCategory = styled.div<{ $isRTL: boolean }>`
  font-size: 0.8rem;
  color: #dc2626;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const ProductTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.2rem;
  color: #0f172a;
  font-weight: 600;
  margin: 0;
  line-height: 1.4;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const TransformateursCategory: React.FC<TransformateursCategoryProps> = ({ t, isRTL }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = React.useState('all');
  const [mobileFiltersOpen, setMobileFiltersOpen] = React.useState(false);

  const breadcrumbs = [
    { label: "Catalogue produits", path: "/catalogue" },
    { label: t.transformersTitle, path: "/category/transformateurs" },
  ];

  const filterCategories = [
    { id: 'all', label: 'Tous', hasDropdown: false },
    { id: 'distribution-huile', label: 'Transformateurs de distribution à huile', hasDropdown: false },
    { id: 'puissance', label: 'Transformateurs de puissance', hasDropdown: false },
  ];

  const products = [
    {
      id: 'novare-residentiel',
      title: 'NOVARE Résidentiel',
      category: 'DISTRIBUTION À HUILE',
      image: '/images/catalog/transformateurs/NOVARE Résidentiel.jpg',
      filter: 'distribution-huile'
    },
    {
      id: 'novare-poteau',
      title: 'NOVARE Poteau',
      category: 'DISTRIBUTION À HUILE',
      image: '/images/catalog/transformateurs/NOVARE Poteau.jpg',
      filter: 'distribution-huile'
    },
    {
      id: 'novare-cabine',
      title: 'NOVARE Cabine',
      category: 'DISTRIBUTION À HUILE',
      image: '/images/catalog/transformateurs/NOVARE Cabine.jpg',
      filter: 'distribution-huile'
    },
    {
      id: 'novare-power',
      title: 'NOVARE Power',
      category: 'PUISSANCE',
      image: '/images/catalog/transformateurs/NOVARE Power.jpg',
      filter: 'puissance'
    }
  ];

  const filteredProducts = activeFilter === 'all' ? products : products.filter(product => product.filter === activeFilter);

  const handleProductClick = (productId: string) => {
    // Make NOVARE Résidentiel unclickable
    if (productId === 'novare-residentiel') {
      return;
    }
    navigate(`/product/${productId}`);
  };

  const handleFilterClick = (filterId: string) => {
    setActiveFilter(filterId);
    setMobileFiltersOpen(false);
  };

  return (
    <CategoryPage $isRTL={isRTL} ref={ref}>
      <Container>
        <HeaderSection $isRTL={isRTL}>
          <Breadcrumbs 
            $isRTL={isRTL}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.label}>
                <BreadcrumbLink 
                  $isLast={index === breadcrumbs.length - 1} 
                  onClick={() => index !== breadcrumbs.length - 1 && navigate(crumb.path)}
                >
                  {crumb.label}
                </BreadcrumbLink>
                {index < breadcrumbs.length - 1 && (
                  <span>{isRTL ? '<' : '>'}</span>
                )}
              </React.Fragment>
            ))}
          </Breadcrumbs>
        </HeaderSection>

        <CategoryTitle 
          $isRTL={isRTL}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
{t.transformersTitle}
        </CategoryTitle>

        <FilterSection $isRTL={isRTL}>
          <MobileFilterToggle 
            $isRTL={isRTL}
            onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46 22,3"></polygon>
            </svg>
            Filtres ({filterCategories.find(f => f.id === activeFilter)?.label || 'Tous'})
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: mobileFiltersOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}>
              <polyline points="6,9 12,15 18,9"></polyline>
            </svg>
          </MobileFilterToggle>
          
          <FilterContainer $isRTL={isRTL} $isOpen={mobileFiltersOpen}>
            {filterCategories.map((filter) => (
              <FilterButton 
                key={filter.id}
                $isRTL={isRTL}
                $active={activeFilter === filter.id}
                $hasDropdown={filter.hasDropdown}
                onClick={() => handleFilterClick(filter.id)}
              >
                {filter.label}
                {filter.hasDropdown && (
                  <DropdownIcon>▼</DropdownIcon>
                )}
              </FilterButton>
            ))}
          </FilterContainer>
          
          <ProductsCount $isRTL={isRTL}>
            {filteredProducts.length} categories
          </ProductsCount>
        </FilterSection>
        
        <ProductsGrid
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={{
            visible: { transition: { staggerChildren: 0.1 } },
            hidden: {  },
          }}
        >
          {filteredProducts.map((product, index) => (
            <ProductCard
              key={product.id}
              $isRTL={isRTL}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 }
              }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              onClick={() => handleProductClick(product.id)}
              style={{
                cursor: product.id === 'novare-residentiel' ? 'default' : 'pointer',
                opacity: product.id === 'novare-residentiel' ? 0.7 : 1
              }}
            >
              <ProductImage>
                <img
                  src={product.image}
                  alt={product.title}
                  loading="lazy"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/images/products/default-product.jpg';
                  }}
                />
              </ProductImage>
              <ProductContent $isRTL={isRTL}>
                <ProductCategory $isRTL={isRTL}>
                  {product.category}
                </ProductCategory>
                <ProductTitle $isRTL={isRTL}>
                  {product.title}
                </ProductTitle>
              </ProductContent>
            </ProductCard>
          ))}
        </ProductsGrid>
      </Container>
    </CategoryPage>
  );
};

export default TransformateursCategory;